import { expressQuerySelectorAll, expressAddClass, expressRemoveClass } from "../../common/html";

export interface IDragAndDropSettings {
	dropElement: HTMLElement;
	overlayElement: HTMLElement;
	/** Disable drag for these elements. To prevent dragging items that are already on the page. */
	noDragElementIdentifiers: string[];
	/** Disable drop for these elements. */
	noDropElementIdentifiers: string[];
	readonly onDrop: (fileList: FileList) => void;
}

export interface IDragAndDropItem {
	readonly enableDropzone: () => void;
	readonly disableDropzone: () => void;
	readonly disableDragEventOnIdentifiers: (identifiers: string[]) => void;
	readonly disableDragEventOnElements: (elements: HTMLElement[]) => void;
	readonly disableDropEventOnIdentifiers: (identifiers: string[]) => void;
	readonly disableDropEventOnElements: (elements: HTMLElement[]) => void;
}

let isEnabled = true;
const enableDropzone = () => {
	isEnabled = true;
};
const disableDropzone = () => {
	isEnabled = false;
};

const disableDragEventOnIdentifiers = (identifiers: string[]) => {
	if (!identifiers) return;

	let elements = new Array<HTMLElement>();
	identifiers.forEach((identifier) => {
		elements = elements.concat(expressQuerySelectorAll<HTMLElement>(document, identifier));
	});

	disableDragEventOnElements(elements);
};
const disableDragEventOnElements = (elements: HTMLElement[]) => {
	if (!elements) return;

	const onDisableDragStart = (e) => {
		e.preventDefault();
		e.stopPropagation();
	};

	elements.forEach((element) => {
		// on dragstart: cancel event and do nothing
		element.removeEventListener("dragstart", onDisableDragStart);
		element.addEventListener("dragstart", onDisableDragStart);
	});
};

const disableDropEventOnIdentifiers = (identifiers: string[]) => {
	if (!identifiers) return;

	let elements = new Array<HTMLElement>();
	identifiers.forEach((identifier) => {
		elements = elements.concat(expressQuerySelectorAll<HTMLElement>(document, identifier));
	});

	disableDropEventOnElements(elements);
};
const disableDropEventOnElements = (elements: HTMLElement[]) => {
	if (!elements) return;

	elements.forEach((element) => {
		// on dragover: cancel event and do nothing
		element.addEventListener("dragover", function (e) {
			e.preventDefault();
		});
		element.addEventListener("drop", function (e) {
			e.preventDefault();
			e.stopPropagation();
		});
	});
};

export function initDragAndDrop(settings: IDragAndDropSettings) {
	if (!settings) return;

	if (!settings.dropElement) return;

	if (!settings.noDragElementIdentifiers) settings.noDragElementIdentifiers = ["img"];

	if (!settings.noDropElementIdentifiers) settings.noDropElementIdentifiers = [".technical-loading-dialog"];

	const hasDedicatedOverlayElement = settings.overlayElement;
	//No overlayElement provided? > the dropElement becomes the overlayElement
	if (!hasDedicatedOverlayElement) settings.overlayElement = settings.dropElement;

	// disable dragging
	disableDragEventOnIdentifiers(settings.noDragElementIdentifiers);

	// on dragenter: show overlay
	settings.dropElement.addEventListener(
		"dragenter",
		function (e) {
			e.preventDefault();
			e.stopPropagation();

			if (!isEnabled) return;

			hasDedicatedOverlayElement && expressRemoveClass(settings.overlayElement, "u-hide");
		},
		false,
	);

	// on dragleave: hide overlay
	settings.overlayElement.addEventListener(
		"dragleave",
		function (e) {
			e.preventDefault();
			e.stopPropagation();

			if (!isEnabled) return;

			hasDedicatedOverlayElement && expressAddClass(settings.overlayElement, "u-hide");
		},
		false,
	);

	// on dragover: cancel event and do nothing
	settings.overlayElement.addEventListener("dragover", function (e) {
		e.preventDefault();
	});

	// on drop: hide overlay and callback
	settings.overlayElement.addEventListener("drop", function (e) {
		e.preventDefault();
		e.stopPropagation();

		if (!isEnabled) return;

		if (e.dataTransfer?.files?.length > 0) {
			settings.onDrop && settings.onDrop(e.dataTransfer.files);

			//Delay to give other code time to render the overlays
			setTimeout(() => {
				disableDropEventOnIdentifiers(settings.noDropElementIdentifiers);
			}, 250);
		}

		hasDedicatedOverlayElement && expressAddClass(settings.overlayElement, "u-hide");
	});

	return {
		enableDropzone,
		disableDropzone,
		disableDragEventOnIdentifiers,
		disableDragEventOnElements,
		disableDropEventOnIdentifiers,
		disableDropEventOnElements,
	} as IDragAndDropItem;
}
